import type React from "react";

import { faArrowRight } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import invariant from "ts-invariant";

import { CreateExerciseIcon, CreateLessonIcon, CreateTemplateIcon } from "assets";
import { useProfileContext } from "contexts/ProfileContext";

import { ContentContainer } from "../components/styles";

export const Create: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile } = useProfileContext();

  invariant(profile, "Profile should be defined");

  return (
    <ContentContainer>
      <CardList>
        <Card title={t("common.exercise")} type="exercise" onClick={() => navigate("../exercises/new")} />
        <Card title={t("common.lesson")} type="lesson" onClick={() => navigate("../lessons/new")} />
        <Card title={t("common.template")} type="template" onClick={() => navigate("../templates/new_template")} />
      </CardList>
    </ContentContainer>
  );
};

const CardList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

export type ContentType = "exercise" | "lesson" | "template";
interface CardProps {
  title: string;
  type: ContentType;
  onClick: () => void;
}

const Card: React.FC<CardProps> = ({ title, type, onClick }) => {
  const theme = useTheme();

  const getIcon = () => {
    if (type === "exercise") return <CreateExerciseIcon />;
    if (type === "lesson") return <CreateLessonIcon />;
    if (type === "template") return <CreateTemplateIcon />;
    return null;
  };

  return (
    <CardContainer onClick={onClick}>
      <Image>{getIcon()}</Image>
      <Title>{title}</Title>
      <FontAwesomeIcon
        icon={faArrowRight}
        color={theme.colors.redesign.b100}
        style={{ fontSize: "24px", marginLeft: "auto" }}
      />
    </CardContainer>
  );
};

const CardContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  height: 112px;
  width: 100%;
  background: ${({ theme }) => theme.colors.redesign.b10};
  border-radius: 16px;
  padding: 16px 24px;
  cursor: pointer;
  ${({ theme }) => theme.aboveBreakpoint} {
    width: calc(100% / 3 - 60px);
  }
`;

const Image = styled.div`
  height: 80px;
`;

const Title = styled.h3`
  font-size: 22px;
  font-style: normal;
  font-weight: 600;
  line-height: 32px;
  letter-spacing: 0.1px;
  margin: 0;
  color: ${({ theme }) => theme.colors.redesign.db100};
`;
